import React, { useEffect, useState } from "react";
import { api, fmtDate, formatErr } from "../../api";
import { SectionTitle, EmptyState, Avatar } from "../../components/Atoms";
import { IdentificationBadge, CheckCircle, X, Prohibit } from "@phosphor-icons/react";
import { toast } from "sonner";

const backendBase = process.env.REACT_APP_BACKEND_URL || "";
const imgSrc = (u) => (u ? (u.startsWith("http") ? u : backendBase + u) : "");

function RejectModal({ ambassador, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="glass w-full max-w-md p-5 relative" onClick={e => e.stopPropagation()} data-testid="reject-id-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-muted hover:text-white"><X size={18} /></button>
        <div className="text-white font-bold text-lg">Reject verification</div>
        <div className="text-xs text-muted mt-1">The creator will see this reason and can re-upload.</div>
        <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Image is blurry, please re-upload a clearer front side"
                  className="input-base mt-3" data-testid="reject-reason-input" />
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary text-sm bg-[#EC4899]" disabled={busy || !reason.trim()}
                  onClick={async () => { setBusy(true); try { await onSubmit(reason); } finally { setBusy(false); } }}
                  data-testid="reject-confirm-btn">
            {busy ? "Rejecting…" : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminVerificationsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/college-id/pending");
      setRows(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (aid) => {
    try {
      await api.post(`/college-id/${aid}/review`, { approve: true });
      toast.success("Approved"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const reject = async (aid, reason) => {
    try {
      await api.post(`/college-id/${aid}/review`, { approve: false, reason });
      toast.success("Rejected"); setRejecting(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-5">
      <SectionTitle title="Verifications" subtitle={`College ID submissions awaiting review (${rows.length})`} />
      {loading ? <div className="skeleton h-40" /> : rows.length === 0 ? (
        <EmptyState icon={IdentificationBadge} title="No pending verifications" hint="You're all caught up." />
      ) : (
        <div className="space-y-3">
          {rows.map(r => (
            <div key={r.id} className="glass p-4" data-testid={`verify-row-${r.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <Avatar src={imgSrc(r.photo)} name={r.name} size={44} />
                <div className="min-w-0 flex-1">
                  <div className="text-white font-bold truncate">{r.name}</div>
                  <div className="text-[11px] text-muted truncate">{r.college || "—"} · {r.city || "—"} · Submitted {fmtDate(r.college_id_submitted_at)}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approve(r.id)} className="btn-primary text-xs flex items-center gap-1.5" data-testid={`verify-approve-${r.id}`}>
                    <CheckCircle size={12} weight="bold" /> Approve
                  </button>
                  <button onClick={() => setRejecting(r)} className="btn-ghost text-xs text-[#EC4899] flex items-center gap-1.5" data-testid={`verify-reject-${r.id}`}>
                    <Prohibit size={12} weight="bold" /> Reject
                  </button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {[["Front", r.college_id_front], ["Back", r.college_id_back]].map(([lab, u]) => (
                  <div key={lab} className="rounded-2xl bg-white/5 border border-white/5 p-3">
                    <div className="text-xs uppercase font-bold text-muted tracking-wider">{lab}</div>
                    <div className="mt-2 h-48 rounded-xl bg-black/30 flex items-center justify-center overflow-hidden">
                      {u ? <img src={imgSrc(u)} alt={lab} className="max-h-full max-w-full object-contain" /> : <span className="text-muted text-xs">Missing</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {rejecting && (
        <RejectModal ambassador={rejecting} onClose={() => setRejecting(null)} onSubmit={(reason) => reject(rejecting.id, reason)} />
      )}
    </div>
  );
}
